import { Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Badge, BadgeSize, BadgeVariant } from "shared/components/Badge";
import { cx } from "shared/utils";
import {
  analyticsDashboardsApi,
  type TouchPointsPerLeadRowItem
} from "api/analytics-dashboards";
import { getErrorMessage } from "utils/error";
import { AnalyticsDetailModal } from "./AnalyticsDetailModal";
import { PropertyGroupHeader } from "./PropertyGroupHeader";
import type { UnitFilterParams } from "./UnitsFilter";
import { LeadType } from "views/leads-id/types";

const ROW_GRID_CLASSNAME = "grid grid-cols-[1fr_130px_90px] items-center gap-4";

const COLUMNS = [
  { label: "Lead" },
  { label: "Unit" },
  { label: "Touch points" }
];

const TouchPointsPerLeadRow = ({
  item
}: {
  item: TouchPointsPerLeadRowItem;
}) => (
  <div className={cx(ROW_GRID_CLASSNAME, "px-5 py-3 bg-content-1000-inverted")}>
    <div className="min-w-0">
      <p className="text-sm font-medium text-content-700 truncate">
        {item.full_name ?? "-"}
      </p>
      {item.email ? (
        <p className="text-xs text-content-300 truncate">{item.email}</p>
      ) : null}
    </div>
    <p className="text-sm font-medium text-content-700 truncate">
      {item.unit_label ?? "-"}
    </p>
    <Badge variant={BadgeVariant.BASE} size={BadgeSize.SM} className="w-fit">
      <span className="!font-bold !text-content-1000">
        {item.touch_points === 0 ? "--" : item.touch_points}
      </span>
    </Badge>
  </div>
);

export const TouchPointsPerLeadModal = ({
  opened,
  onClose,
  start,
  finish,
  filterParams,
  leadType,
  excludeInviteSources
}: {
  opened: boolean;
  onClose: () => void;
  start?: string;
  finish?: string;
  filterParams?: UnitFilterParams;
  leadType?: LeadType;
  excludeInviteSources?: boolean;
}) => {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "ANALYTICS_DASHBOARDS",
      "MODULE_ROWS",
      "crm_touch_points_per_lead",
      start,
      finish,
      filterParams,
      leadType,
      excludeInviteSources
    ],
    queryFn: () =>
      analyticsDashboardsApi.getTouchPointsPerLeadRows({
        start,
        finish,
        ...filterParams,
        lead_type: leadType,
        ...(excludeInviteSources ? { exclude_invite_sources: true } : {})
      }),
    enabled: opened,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const groups = data?.groups ?? [];

  return (
    <AnalyticsDetailModal
      opened={opened}
      onClose={onClose}
      title="Touch points per lead"
      gridClassName={ROW_GRID_CLASSNAME}
      columns={COLUMNS}
      isLoading={isLoading}
      isFetching={isFetching}
      isEmpty={groups.length === 0}
      emptyText="No records found"
      pagination={{ total: 0, current: 1, onChange: () => {} }}
      showPagination={false}
    >
      {groups.map((group) => (
        <Fragment key={group.key}>
          <PropertyGroupHeader name={group.label} />
          {group.items.map((item) => (
            <TouchPointsPerLeadRow key={item.lead_id} item={item} />
          ))}
        </Fragment>
      ))}
    </AnalyticsDetailModal>
  );
};
